import { supabase } from '@/integrations/supabase/client';

export interface ConversationMemoryData {
  conversationId: string;
  summary: string;
  title: string;
  keyTopics: string[];
  importanceScore?: number;
}

export interface SimilarConversation {
  conversation_id: string;
  content_summary: string;
  conversation_title: string;
  key_topics: string[];
  importance_score: number;
  similarity: number;
  created_at: string;
}

export class ConversationMemoryService {
  /**
   * Cria uma memória de conversa com embedding
   * Esta função é opcional - se falhar, não afeta o fluxo principal
   */
  // ⚡ OTIMIZADO: timeout de 3s e skip automático em caso de erro
  static async createMemory(
    userId: string, 
    data: ConversationMemoryData
  ): Promise<void> {
    try {
      console.log('💾 Tentando criar memória de conversa...');
      
      // ⚡ Promise com timeout de 3s para não travar o sistema principal
      const memoryPromise = supabase.functions.invoke('conversation-memory', {
        body: {
          action: 'create',
          conversationId: data.conversationId,
          userId,
          summary: data.summary,
          title: data.title,
          keyTopics: data.keyTopics,
          importanceScore: data.importanceScore || 0.5
        }
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout - memória opcional')), 3000)
      );

      const { data: response, error } = await Promise.race([memoryPromise, timeoutPromise]) as any;

      if (error) {
        console.warn('⚠️ Erro ao criar memória (funcionalidade opcional - sistema continua):', error.message);
        return; // Falha silenciosa - não afeta fluxo principal
      }

      // Verificar se a resposta indica erro de configuração
      if (response?.error && response?.message?.includes('temporariamente indisponível')) {
        console.warn('💡 Funcionalidade de memória semântica temporariamente indisponível');
        return;
      }
      
      console.log('✅ Memória de conversa criada com sucesso');
    } catch (error: any) {
      console.warn('⚠️ Falha ao criar memória (funcionalidade opcional - sistema continua):', error.message);
      // Falha 100% silenciosa - não quebra o fluxo principal sob nenhuma circunstância
    }
  }

  /**
   * Busca conversas similares por contexto
   * Esta função é opcional - se falhar, retorna array vazio
   */
  // ⚡ OTIMIZADO: timeout de 2s e skip automático
  static async searchSimilarConversations(
    userId: string,
    query: string
  ): Promise<SimilarConversation[]> {
    try {
      console.log('🔍 Tentando buscar conversas similares...');
      
      // ⚡ Promise com timeout de 2s para não travar o sistema principal  
      const searchPromise = supabase.functions.invoke('conversation-memory', {
        body: {
          action: 'search',
          userId,
          query
        }
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout - busca opcional')), 2000)
      );

      const { data, error } = await Promise.race([searchPromise, timeoutPromise]) as any;

      if (error) {
        console.warn('⚠️ Erro ao buscar conversas (funcionalidade opcional - sistema continua):', error.message);
        return []; // Falha silenciosa
      }

      // Verificar se a resposta indica erro de configuração
      if (data?.error && data?.message?.includes('temporariamente indisponível')) {
        console.warn('💡 Funcionalidade de busca semântica temporariamente indisponível');
        return [];
      }

      const conversations = data?.conversations || [];
      console.log(`✅ Encontradas ${conversations.length} conversas similares`);
      return conversations;
    } catch (error: any) {
      console.warn('⚠️ Falha na busca (funcionalidade opcional - sistema continua):', error.message);
      return []; // Falha 100% silenciosa - não bloqueia o sistema principal
    }
  }

  /**
   * Analisa uma conversa e extrai dados para criar memória
   */
  static analyzeConversationForMemory(
    conversationTitle: string,
    messages: Array<{ role: string; content: string }>
  ): {
    summary: string;
    keyTopics: string[];
    importanceScore: number;
  } {
    // Filtrar apenas mensagens do usuário e assistente
    const relevantMessages = messages.filter(
      msg => msg.role === 'user' || msg.role === 'assistant'
    );

    if (relevantMessages.length === 0) {
      return {
        summary: '',
        keyTopics: [],
        importanceScore: 0.1
      };
    }

    // Criar resumo dos principais pontos da conversa
    const userMessages = relevantMessages
      .filter(msg => msg.role === 'user')
      .map(msg => msg.content)
      .join(' ');

    const assistantMessages = relevantMessages
      .filter(msg => msg.role === 'assistant')
      .map(msg => msg.content)
      .join(' ');

    // Extrair tópicos-chave baseado em palavras importantes
    const keywordsRegex = /\b(contrato|petição|processo|recurso|sentença|jurisprudência|lei|artigo|código|súmula|precedente|prazo|audiência|liminar|mandado|ação|defesa|apelação|embargos|habeas|mandamus|injunção|inconstitucionalidade)\b/gi;
    
    const userKeywords = userMessages.match(keywordsRegex) || [];
    const assistantKeywords = assistantMessages.match(keywordsRegex) || [];
    
    const allKeywords = [...userKeywords, ...assistantKeywords];
    const uniqueKeywords = [...new Set(allKeywords.map(k => k.toLowerCase()))];

    // Calcular score de importância baseado em fatores
    let importanceScore = 0.5; // Base

    // Mais mensagens = mais importante
    if (relevantMessages.length > 10) importanceScore += 0.2;
    else if (relevantMessages.length > 5) importanceScore += 0.1;

    // Palavras-chave jurídicas = mais importante
    if (uniqueKeywords.length > 5) importanceScore += 0.2;
    else if (uniqueKeywords.length > 2) importanceScore += 0.1;

    // Documentos ou análises = mais importante
    if (userMessages.includes('documento') || userMessages.includes('análise')) {
      importanceScore += 0.1;
    }

    // Limitar entre 0 e 1
    importanceScore = Math.min(1, Math.max(0, importanceScore));

    const summary = `Conversa sobre ${conversationTitle}: ${userMessages.substring(0, 200)}${userMessages.length > 200 ? '...' : ''}`;

    return {
      summary,
      keyTopics: uniqueKeywords.slice(0, 10), // Máximo 10 tópicos
      importanceScore
    };
  }
}